<template>
  <AuthLayout
    mode="register"
    sidebar-title="Create your account"
    sidebar-text="Register as a patient with your referral code from your doctor."
    :support-phone="supportPhone"
    :progress-steps="patientSteps"
    :progress-current-step="1"
    title="Patient Registration"
    subtitle="Please fill out the entire form."
    :classic="classic"
  >
    <div v-if="showVideo && videoUrl" class="auth-register__video">
      <button type="button" class="auth-login__link auth-register__video-link" @click="videoOpen = !videoOpen">
        How to Register
      </button>
      <div v-if="videoOpen" class="auth-register__video-panel">
        <h3 v-if="videoTitle">{{ videoTitle }}</h3>
        <video class="auth-register__video-player" controls :poster="videoPoster">
          <source :src="videoUrl" type="video/mp4">
        </video>
      </div>
    </div>

    <form class="auth-register__form" @submit.prevent="handleSubmit">
      <AuthField
        v-if="classic"
        id="referral-code"
        v-model="form.referral_code"
        label="Referral Code *"
        autocomplete="off"
        :error="fieldErrors.referral_code || referralError"
        required
      />

      <AuthField
        v-if="classic && companyName"
        id="company-name"
        :model-value="companyName"
        label="Practice Name *"
        disabled
        readonly
      />

      <AuthSelect
        id="salutation"
        name="salutation"
        v-model="form.salutation"
        label="Salutation *"
        placeholder="Select salutation"
        :options="salutationOptions"
        :error="fieldErrors.salutation"
        required
      />

      <AuthField
        id="first-name"
        v-model="form.first_name"
        label="First Name *"
        autocomplete="given-name"
        input-mask="name"
        :error="fieldErrors.first_name"
        required
      />

      <AuthField
        id="last-name"
        v-model="form.last_name"
        label="Last Name *"
        autocomplete="family-name"
        input-mask="name"
        :error="fieldErrors.last_name"
        required
      />

      <AuthSelect
        v-if="!classic"
        id="gender"
        name="gender"
        v-model="form.gender"
        label="Gender *"
        placeholder="Select gender"
        :options="genderOptions"
        :error="fieldErrors.gender"
        required
      />

      <AuthField
        v-if="!classic"
        id="dob"
        v-model="form.dob"
        label="Date of Birth *"
        placeholder="MM/DD/YYYY"
        input-mask="date"
        autocomplete="bday"
        :error="fieldErrors.dob"
        required
      />

      <AuthField
        id="phone-number"
        v-model="form.phone_number"
        :label="classic ? 'Mobile phone *' : 'Phone Number *'"
        type="tel"
        input-mask="phone"
        placeholder="000-000-0000"
        autocomplete="tel"
        :error="fieldErrors.phone_number"
        required
      />

      <AuthField
        id="email"
        v-model="form.email"
        label="Email *"
        type="email"
        autocomplete="email"
        :error="fieldErrors.email"
        required
      />

      <AuthField
        v-if="classic"
        id="dob"
        v-model="form.dob"
        label="Date of Birth *"
        placeholder="MM/DD/YYYY"
        input-mask="date"
        autocomplete="bday"
        :error="fieldErrors.dob"
        required
      />

      <AuthPasswordField
        id="password"
        v-model="form.password"
        label="Password *"
        autocomplete="new-password"
        :error="fieldErrors.password"
        :classic="classic"
        required
        @update:model-value="clearPasswordMismatchError"
      />
      <AuthPasswordMeter
        :password="form.password"
        :email="form.email"
        :first-name="form.first_name"
        :last-name="form.last_name"
      />

      <AuthPasswordField
        id="password-confirm"
        v-model="form.password_confirmation"
        label="Confirm Password *"
        autocomplete="new-password"
        :error="fieldErrors.password_confirmation || passwordConfirmationError"
        :classic="classic"
        required
        @update:model-value="clearPasswordMismatchError"
      />

      <div class="auth-register__consents">
        <AuthCheckbox
          id="privacy-policy-consent"
          v-model="form.privacy_policy_consent"
          :error="fieldErrors.privacy_policy_consent"
        >
          I agree to the
          <a :href="consentLink" target="_blank" rel="noopener" class="auth-login__link">Terms of Service and Privacy Policy and consent to RPM/PCM services</a>
          from my Provider through RemoniHealth.
        </AuthCheckbox>

        <AuthCheckbox
          id="notifications-consent"
          v-model="form.notifications_consent"
        >
          <span class="classic-complete__consent-sms">
            <span>By checking this box, I agree to receive automated and manual text messages from RemoniHealth about care continuity.</span>
            <span class="classic-complete__consent-sms-frequency">Message frequency varies. Msg & data rates may apply. Reply STOP to cancel.</span>
          </span>
        </AuthCheckbox>
      </div>

      <span v-if="formError" class="auth-login__error-block" role="alert">{{ formError }}</span>

      <div class="auth-login__actions">
        <AuthButton type="submit" :loading="loading">Register</AuthButton>
      </div>
    </form>

    <template v-if="!classic" #footer>
      <p class="auth-login__signup-hint">
        Already have an account?
        <a :href="loginUrl" class="auth-login__link auth-login__link--plain">Sign in</a>
      </p>
    </template>
  </AuthLayout>
</template>

<script setup>
import { computed, ref, reactive, toRef, onMounted, nextTick, watch } from 'vue'
import AuthLayout from '../../shared/components/AuthLayout.vue'
import AuthField from '../../shared/components/AuthField.vue'
import AuthSelect from '../../shared/components/AuthSelect.vue'
import AuthPasswordField from '../../shared/components/AuthPasswordField.vue'
import AuthPasswordMeter from '../../shared/components/AuthPasswordMeter.vue'
import {
  isPasswordValid,
  PASSWORD_CONFIRMATION_MISMATCH_MESSAGE,
} from '../../shared/utils/passwordPolicy.js'
import AuthButton from '../../shared/components/AuthButton.vue'
import AuthCheckbox from '../../shared/components/AuthCheckbox.vue'
import { PATIENT_STEPS } from '../constants/steps.js'
import { useReferralLookup } from '../composables/useReferralLookup.js'

const props = defineProps({
  loading:            { type: Boolean, default: false },
  formError:          { type: String, default: '' },
  fieldErrors:        { type: Object, default: () => ({}) },
  initialCode:        { type: String, default: '' },
  initialCompanyName: { type: String, default: '' },
  initialReferralError: { type: String, default: '' },
  salutations:        { type: Array, default: () => [] },
  genders:            { type: Array, default: () => [] },
  termsLink:          { type: String, default: '' },
  privacyLink:        { type: String, default: '' },
  consentLink:        { type: String, default: '' },
  loginUrl:           { type: String, default: '/login' },
  supportPhone:       { type: String, default: '815-908-2020' },
  showVideo:          { type: Boolean, default: false },
  videoUrl:           { type: String, default: '' },
  videoTitle:         { type: String, default: '' },
  videoPoster:        { type: String, default: '' },
  classic:            { type: Boolean, default: false },
})

const emit = defineEmits(['submit'])

const patientSteps = PATIENT_STEPS

const form = reactive({
  referral_code: props.initialCode,
  salutation: '',
  gender: '',
  first_name: '',
  last_name: '',
  phone_number: '',
  email: '',
  dob: '',
  password: '',
  password_confirmation: '',
  privacy_policy_consent: false,
  notifications_consent: false,
})

Object.keys(form).forEach(key => {
  watch(() => form[key], () => {
    if (props.fieldErrors[key]) {
      props.fieldErrors[key] = ''
    }
  })
})

const companyName = ref(props.initialCompanyName)
const referralError = ref(props.initialReferralError)
const videoOpen = ref(false)
const passwordConfirmationError = ref('')

const salutationOptions = props.salutations.map((value) => ({
  value,
  label: value,
}))

const genderOptions = props.genders.map(({ value, label }) => ({
  value: String(value),
  label,
}))

const genderBySalutation = {
  'Mr.': '0',
  'Ms.': '1',
  'Mrs.': '1',
  'Dr.': '3',
  'Mx.': '2',
}

watch(() => form.salutation, (salutation) => {
  const mappedGender = genderBySalutation[salutation]
  if (mappedGender !== undefined) {
    form.gender = mappedGender
  }
})

useReferralLookup(toRef(form, 'referral_code'), ({ companyName: name, error }) => {
  companyName.value = name
  referralError.value = error
})

watch(() => props.initialCode, (code) => {
  if (code) form.referral_code = code
})

const passwordContext = computed(() => ({
  email: form.email,
  firstName: form.first_name,
  lastName: form.last_name,
}))

const clearPasswordMismatchError = () => {
  passwordConfirmationError.value = ''
}

const handleSubmit = () => {
  passwordConfirmationError.value = ''

  if (!isPasswordValid(form.password, passwordContext.value) || !form.password_confirmation) {
    return
  }

  if (form.password !== form.password_confirmation) {
    passwordConfirmationError.value = PASSWORD_CONFIRMATION_MISMATCH_MESSAGE
    return
  }

  emit('submit', {
    ...form,
    privacy_policy_consent: form.privacy_policy_consent ? '1' : '0',
    notifications_consent: form.notifications_consent ? '1' : '0',
  })
}
</script>

<style scoped>
.classic-complete__consent-sms { display: flex; flex-direction: column; gap: 10px; }
.classic-complete__consent-sms-frequency { font-size: 16px; }
</style>
